import { cn } from "@/lib/utils";
import { forwardRef } from "react";

// ============================================
// 타입 정의
// ============================================

/** Figma: checkbox-button — size = 24px / 20px / 16px */
export type CheckboxButtonSize = 24 | 20 | 16;

interface CheckboxButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  size?: CheckboxButtonSize;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}

// ============================================
// Figma 디자인 토큰
// ============================================

/** Box 사이즈 (Figma number/24, number/20, number/16) */
const boxSizeStyles: Record<CheckboxButtonSize, string> = {
  24: "size-6",
  20: "size-5",
  16: "size-4",
};

/** Glyph 사이즈 — Figma 원본은 24px box 안 18px glyph(=75%)다. */
const glyphSizeStyles: Record<CheckboxButtonSize, string> = {
  24: "size-[18px]",
  20: "size-[15px]",
  16: "size-3",
};

/** Figma 원본 vector(18×18) — 둥근 사각형 테두리. */
const BOX_PATH =
  "M2 0C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2V16C0 16.5304 0.210714 17.0391 0.585786 17.4142C0.960859 17.7893 1.46957 18 2 18H16C16.5304 18 17.0391 17.7893 17.4142 17.4142C17.7893 17.0391 18 16.5304 18 16V2C18 1.46957 17.7893 0.960859 17.4142 0.585786C17.0391 0.210714 16.5304 0 16 0H2Z";

/** unchecked 는 테두리만 남기는 2px 링(evenodd 로 안쪽을 뚫는다). */
const BOX_INNER_PATH = "M2 2H16V16H2V2Z";

/** checked 일 때 채워진 사각형 위에 얹는 체크 표시. */
const CHECK_PATH =
  "M13.95 6.796C14.1376 6.60849 14.2431 6.35412 14.2432 6.08885C14.2433 5.82358 14.138 5.56914 13.9505 5.3815C13.763 5.19386 13.5086 5.08839 13.2434 5.0883C12.9781 5.0882 12.7236 5.19349 12.536 5.381L7.586 10.331L5.465 8.21C5.37216 8.11709 5.26192 8.04338 5.14059 7.99307C5.01926 7.94276 4.8892 7.91684 4.75785 7.9168C4.49258 7.9167 4.23814 8.02199 4.0505 8.2095C3.86286 8.39701 3.75739 8.65138 3.7573 8.91665C3.7572 9.18192 3.86249 9.43636 4.05 9.624L6.808 12.382C6.91015 12.4842 7.03144 12.5653 7.16493 12.6206C7.29842 12.6759 7.4415 12.7044 7.586 12.7044C7.7305 12.7044 7.87358 12.6759 8.00707 12.6206C8.14056 12.5653 8.26185 12.4842 8.364 12.382L13.95 6.796Z";

// ============================================
// 컴포넌트
// ============================================

/**
 * Figma `checkbox-button` 컴포넌트.
 * - unchecked: Label/Normal + opacity 24%
 * - checked: Primary Blue(#2B7FFF) 채움 + 흰 체크
 * - disabled: opacity 8%
 */
export const CheckboxButton = forwardRef<
  HTMLButtonElement,
  CheckboxButtonProps
>(
  (
    {
      size = 20,
      checked = false,
      disabled = false,
      className,
      onChange,
      onClick,
      ...props
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      onChange?.(!checked);
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "inline-flex shrink-0 items-center justify-center focus-visible:outline-none",
          boxSizeStyles[size],
          disabled ? "cursor-not-allowed opacity-8" : "cursor-pointer",
          !disabled && !checked && "opacity-24",
          className,
        )}
        {...props}
      >
        <svg
          className={cn(glyphSizeStyles[size])}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          {checked ? (
            <>
              <path d={BOX_PATH} className="fill-blue-500" />
              <path d={CHECK_PATH} className="fill-static-white" />
            </>
          ) : (
            <path
              d={`${BOX_PATH}${BOX_INNER_PATH}`}
              fillRule="evenodd"
              clipRule="evenodd"
              className="fill-label-normal"
            />
          )}
        </svg>
      </button>
    );
  },
);

CheckboxButton.displayName = "CheckboxButton";

export default CheckboxButton;
